import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  Divider,
  Avatar,
  useTheme,
  useMediaQuery,
  SwipeableDrawer,
  Tooltip,
  Badge as MuiUnreadBadge,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Message as MessageIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  WhatsApp as WhatsAppIcon,
  QrCode as QrCodeIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Send as SendIcon,
  CampaignOutlined as FollowUpIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Business as BusinessIcon,
  ViewModule as AdSetIcon,
  Article as AdsIcon,
  PlaylistAdd as BulkIcon,
  AccountBalance as AccountBalanceIcon,
  AttachMoney as RevenueIcon,
  TrendingDown as ExpenseIcon,
  ViewKanban as KanbanIcon,
  IntegrationInstructions as IntegrationIcon,
  Support as SupportIcon,
  CloudQueue as CloudIcon,
  AutoAwesome as AutoAwesomeIcon,
  CalendarToday as CalendarTodayIcon,
  CalendarViewWeek as CalendarViewWeekIcon,
  LocalHospital as ClinicIcon,
  MedicalServices as MedicalServicesIcon,
  EventNote as AppointmentIcon,
  FolderShared as RecordsIcon,
  Healing as ProceduresIcon,
  Badge as ProfessionalsIcon,
  Inventory as InventoryIcon,
  Receipt as ReceiptIcon,
  Description as PrescriptionIcon,
  Science as ExamOrderIcon,
  Biotech as LabIcon,
  VerifiedUser as CertificateIcon,
  Shortcut as ReferralIcon,
  ReceiptLong as ReceiptsIcon,
  HealthAndSafety as InsuranceIcon,
  Percent as CommissionIcon,
  EventSeat as WaitingRoomIcon,
  PhotoLibrary as GalleryIcon,
  AccountTree as TreatmentPlanIcon,
  Analytics as ReportsIcon,
  NotificationsActive as ReminderIcon,
  Draw as SignatureIcon,
  Timeline as EvolutionIcon,
  PointOfSale as PointOfSaleIcon,
  Inventory as InventoryModIcon,
  ShoppingCart as ProductIcon,
  Build as MaterialMenuIcon,
  LocalPharmacy as MedicationIcon,
  HealthAndSafety as InsuranceMedIcon,
  ViewModule as KitMenuIcon,
  Place as LocationMenuIcon,
  Factory as ManufacturerIcon,
  SwapVert as MovementIcon,
  Category as CategoryIcon,
  Policy as AuditIcon,
  AccountBalanceWallet as WalletIcon,
  SwapHoriz as RepasseIcon,
  CreditCard as CreditCardIcon,
  RequestQuote as InvoiceIcon,
  Store as StoreIcon,
  MoneyOff as DiscountIcon,
  WorkOutline as HonorariosIcon,
  CallReceived as CheckInIcon,
  CallMade as CheckOutIcon,
  Payments as PaymentsIcon,
  Repeat as RecurringIcon,
  TrendingUp as TrendingUpIcon,
  TripOriginOutlined as TripOriginOutlinedIcon,
  EventAvailable as DoctoraliaIcon,
} from '@mui/icons-material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { usePWA } from '../../hooks/usePWA';
import LogoutButton from '../LogoutButton';
import NotificationPermissionDialog from '../NotificationPermissionDialog';
import useNotifications from '../../hooks/useNotifications';
import PaymentNotificationBanner from '../PaymentNotificationBanner';
import { usePaymentNotification } from '../../contexts/PaymentNotificationContext';
import { inboxService, InboxMenuItem } from '../../services/inboxService';
import { useAuth } from '../../contexts/AuthContext';
import { SystemRole, hasRoutePermission, getRoleLabel, ROLE_COLORS } from '../../config/permissions';
import { getWebSocketUrl } from '../../config/environment';
import UnitSwitcher from './UnitSwitcher';
import VirtualAssistantWidget from '../support/VirtualAssistantWidget';

const drawerWidth = 240;
const drawerWidthCollapsed = 88;
/** Deslocamento horizontal do paper do menu (deve entrar na largura reservada no flex) */
const drawerMarginLeft = 16;
/** Folga entre menu e área principal */
const mainContentGap = 16;

const MainLayout: React.FC = () => {
  const { notification } = usePaymentNotification();
  const bannerHeight = notification.active ? 32 : 0;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  const [whatsappAutoOpen, setWhatsappAutoOpen] = useState(false);
  const [automationsOpen, setAutomationsOpen] = useState(false);
  const [attendantsOpen, setAttendantsOpen] = useState(false);
  const [clinicOpen, setClinicOpen] = useState(false);
  const [financeiroOpen, setFinanceiroOpen] = useState(false);
  const [estoqueOpen, setEstoqueOpen] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [dynamicInboxes, setDynamicInboxes] = useState<InboxMenuItem[]>([]);
  const [inboxesLoaded, setInboxesLoaded] = useState(false);
  const [inboxUnreadByChannel, setInboxUnreadByChannel] = useState<Record<string, number>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isMobile: pwaIsMobile, isStandalone } = usePWA();
  const {
    shouldShowPermissionDialog,
    markPermissionDialogShown,
    isSupported: notificationSupported
  } = useNotifications();

  // Carregar inboxes dinâmicos da API; recarrega ao trocar de unidade
  useEffect(() => {
    const loadDynamicInboxes = async (forceRefresh = false) => {
      try {
        const menuData = await inboxService.getMenuInboxes(forceRefresh);
        setDynamicInboxes(menuData.all || []);
        setInboxesLoaded(true);
        try {
          const unread = await inboxService.getUnreadCounts();
          setInboxUnreadByChannel(unread.byChannel || {});
        } catch {
          /* não bloqueia menu */
        }
      } catch (error) {
        console.error('Erro ao carregar inboxes dinâmicos:', error);
        setDynamicInboxes([
          {
            id: 'default-onboarding',
            text: 'WhatsApp 1',
            path: '/inbox/whatsapp',
            type: 'onboarding',
            provider: 'evolution_api',
            isDefault: true,
            channelType: 'whatsapp',
            icon: 'whatsapp',
            badge: null
          },
        ]);
        setInboxesLoaded(true);
      }
    };

    loadDynamicInboxes();

    // Quando o usuário trocar de unidade, invalidar o cache e recarregar
    const handleUnitChanged = () => {
      inboxService.invalidateCache();
      loadDynamicInboxes(true);
    };
    window.addEventListener('unit:changed', handleUnitChanged);
    return () => window.removeEventListener('unit:changed', handleUnitChanged);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerExpand = () => {
    setDrawerOpen(!drawerOpen);
  };

  // Auto-close mobile drawer when route changes
  useEffect(() => {
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Auto-close submenus on mobile when drawer closes
  useEffect(() => {
    if (!mobileOpen && isMobile) {
      setInboxOpen(false);
      setCampaignsOpen(false);
      setWhatsappAutoOpen(false);
      setAutomationsOpen(false);
      setAttendantsOpen(false);
      setClinicOpen(false);
      setFinanceiroOpen(false);
      setEstoqueOpen(false);
    }
  }, [mobileOpen, isMobile]);

  // Auto-open submenus when on their routes
  useEffect(() => {
    if (location.pathname.startsWith('/automations')) {
      setAutomationsOpen(true);
    }
    if (location.pathname.startsWith('/attendants')) {
      setAttendantsOpen(true);
    }
    if (location.pathname.startsWith('/clinic') && !location.pathname.startsWith('/clinic/reports')) {
      setClinicOpen(true);
    }
    if (location.pathname.startsWith('/financial')) {
      setFinanceiroOpen(true);
    }
    if (location.pathname.startsWith('/inventory')) {
      setEstoqueOpen(true);
    }
  }, [location.pathname]);

  // Mostrar dialog de notificações quando necessário
  useEffect(() => {
    if (shouldShowPermissionDialog && isStandalone) {
      const timer = setTimeout(() => {
        setShowNotificationDialog(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [shouldShowPermissionDialog, isStandalone]);

  // Callbacks do dialog de notificações
  const handleNotificationPermissionGranted = () => {
    markPermissionDialogShown();
    console.log('Notificações ativadas com sucesso!');
  };

  const handleNotificationPermissionDenied = () => {
    markPermissionDialogShown();
    console.log('Notificações recusadas pelo usuário');
  };

  const handleCloseNotificationDialog = () => {
    setShowNotificationDialog(false);
  };

  // Submenu de inbox: somente inboxes dinâmicos da unidade ativa (isDefault=false).
  // Sem inbox dinâmico para a unidade → submenu vazio → "Central de Mensagens"
  // some do menu (filtro `submenu.length > 0` mais abaixo).
  const buildInboxSubmenu = () => {
    if (!inboxesLoaded) return [];

    const dynamicOnboarding = dynamicInboxes.filter(
      (inbox) => inbox.type === 'onboarding' && !inbox.isDefault,
    );

    return dynamicOnboarding.map((inbox) => {
      const cid = inbox.channelId;
      const unreadCount = cid ? inboxUnreadByChannel[cid] ?? 0 : 0;
      return {
        text: inbox.text,
        icon: <WhatsAppIcon />,
        path: inbox.path,
        badge: inbox.badge,
        type: inbox.type,
        channelId: cid,
        unreadCount,
      };
    });
  };

  // Obter dados do auth
  const { user: authUser, isAdmin: isUserAdmin, roleLabel: userRoleLabel, hasPermission } = useAuth();
  const userRole = authUser?.role || 'admin';

  const refreshInboxUnreadCounts = useCallback(async () => {
    if (!inboxesLoaded || !hasPermission('/inbox')) return;
    try {
      const data = await inboxService.getUnreadCounts();
      setInboxUnreadByChannel(data.byChannel ?? {});
    } catch (e) {
      console.error('Erro ao carregar não lidas da Central:', e);
    }
  }, [inboxesLoaded, hasPermission]);

  useEffect(() => {
    void refreshInboxUnreadCounts();
  }, [refreshInboxUnreadCounts]);

  useEffect(() => {
    const id = window.setInterval(() => void refreshInboxUnreadCounts(), 35000);
    return () => window.clearInterval(id);
  }, [refreshInboxUnreadCounts]);

  useEffect(() => {
    if (location.pathname.startsWith('/inbox')) {
      void refreshInboxUnreadCounts();
    }
  }, [location.pathname, refreshInboxUnreadCounts]);

  const refreshInboxUnreadCountsRef = useRef(refreshInboxUnreadCounts);
  refreshInboxUnreadCountsRef.current = refreshInboxUnreadCounts;

  const inboxUnreadDebounceRef = useRef<number | null>(null);
  const inboxLayoutSocketRef = useRef<Socket | null>(null);

  const scheduleInboxUnreadRefreshFromRealtime = useCallback(() => {
    if (inboxUnreadDebounceRef.current) {
      window.clearTimeout(inboxUnreadDebounceRef.current);
    }
    inboxUnreadDebounceRef.current = window.setTimeout(() => {
      inboxUnreadDebounceRef.current = null;
      void refreshInboxUnreadCountsRef.current();
    }, 300);
  }, []);

  /** Atualiza contadores do menu quando o backend emite mensagem/conversa (Socket.IO já faz broadcast global). */
  useEffect(() => {
    if (!hasPermission('/inbox')) {
      if (inboxLayoutSocketRef.current) {
        inboxLayoutSocketRef.current.removeAllListeners();
        inboxLayoutSocketRef.current.disconnect();
        inboxLayoutSocketRef.current = null;
      }
      return;
    }

    const wsUrl = getWebSocketUrl();
    if (!wsUrl?.trim()) {
      return;
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!token) {
      return;
    }

    const socket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      auth: { token },
    });

    inboxLayoutSocketRef.current = socket;

    const bump = () => scheduleInboxUnreadRefreshFromRealtime();
    socket.on('new_message', bump);
    socket.on('conversation_updated', bump);

    return () => {
      socket.off('new_message', bump);
      socket.off('conversation_updated', bump);
      socket.disconnect();
      if (inboxLayoutSocketRef.current === socket) {
        inboxLayoutSocketRef.current = null;
      }
      if (inboxUnreadDebounceRef.current) {
        window.clearTimeout(inboxUnreadDebounceRef.current);
        inboxUnreadDebounceRef.current = null;
      }
    };
  }, [scheduleInboxUnreadRefreshFromRealtime]);

  useEffect(() => {
    const onInboxMenuUnreadRefresh = () => scheduleInboxUnreadRefreshFromRealtime();
    window.addEventListener('inbox:menu-unread-refresh', onInboxMenuUnreadRefresh as EventListener);
    return () => window.removeEventListener('inbox:menu-unread-refresh', onInboxMenuUnreadRefresh as EventListener);
  }, [scheduleInboxUnreadRefreshFromRealtime]);

  // Filtrar itens de menu baseado no perfil do usuário
  const filterByRole = (items: any[]) => {
    return items.filter((item: any) => {
      if (item.path) return hasPermission(item.path);
      if (item.roles) return item.roles.includes(userRole);
      return true;
    });
  };

  const cashRegisterPath = '/financial/cash-register';
  const showCashRegisterShortcut = hasPermission(cashRegisterPath);
  const isCashRegisterActive =
    location.pathname === cashRegisterPath ||
    location.pathname.startsWith(`${cashRegisterPath}/`);

  const allMenuItems = [
    { text: 'Agenda', icon: <CalendarTodayIcon />, path: '/agenda', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[] },
    { text: 'Agenda Expandida', icon: <CalendarViewWeekIcon />, path: '/agenda-expandida', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[] },
    {
      text: 'Central de Mensagens',
      icon: <MessageIcon />,
      hasSubmenu: true,
      isOpen: inboxOpen,
      toggle: () => setInboxOpen(!inboxOpen),
      submenu: buildInboxSubmenu(),
      roles: ['admin', 'admin_master', 'receptionist'] as SystemRole[],
    },
    { text: 'Central de Atendimento', icon: <SupportIcon />, path: '/support', roles: ['admin', 'admin_master'] as SystemRole[] },
    { text: 'Pacientes', icon: <PeopleIcon />, path: '/clinic/patients', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'assistant'] as SystemRole[] },
    { text: 'Evoluções', icon: <EvolutionIcon />, path: '/clinic/evolutions', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'] as SystemRole[] },
    { text: 'Orçamentos', icon: <ReceiptIcon />, path: '/clinic/budgets', roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial'] as SystemRole[] },
    {
      text: 'Clínica',
      icon: <MedicalServicesIcon />,
      hasSubmenu: true,
      isOpen: clinicOpen,
      toggle: () => setClinicOpen(!clinicOpen),
      roles: ['admin', 'admin_master', 'professional', 'receptionist', 'financial', 'assistant'] as SystemRole[],
      submenu: filterByRole([
        { text: 'Dashboard Conversas', icon: <DashboardIcon />, path: '/dashboard', roles: ['admin', 'admin_master'] as SystemRole[] },
        { text: 'Prontuário', icon: <RecordsIcon />, path: '/clinic/medical-records' },
        { text: 'Procedimentos', icon: <ProceduresIcon />, path: '/clinic/procedures' },
        { text: 'Origens de pacientes', icon: <TripOriginOutlinedIcon sx={{ fontSize: 22 }} />, path: '/clinic/patient-sources' },
        { text: 'Profissionais', icon: <ProfessionalsIcon />, path: '/clinic/professionals' },
        { text: 'Receituários', icon: <PrescriptionIcon />, path: '/clinic/prescriptions' },
        { text: 'Pedidos de Exames', icon: <ExamOrderIcon />, path: '/clinic/exam-orders' },
        { text: 'Laboratório', icon: <LabIcon />, path: '/clinic/laboratory' },
        { text: 'Atestados', icon: <CertificateIcon />, path: '/clinic/certificates' },
        { text: 'Encaminhamentos', icon: <ReferralIcon />, path: '/clinic/referrals' },
        { text: 'Convênios / TISS', icon: <InsuranceIcon />, path: '/clinic/insurance' },
        { text: 'Sala de Espera', icon: <WaitingRoomIcon />, path: '/clinic/waiting-room' },
        { text: 'Galeria Clínica', icon: <GalleryIcon />, path: '/clinic/image-gallery' },
        { text: 'Confirmação Auto', icon: <ReminderIcon />, path: '/clinic/reminder-settings' },
        { text: 'Assinatura Digital', icon: <SignatureIcon />, path: '/clinic/digital-signature' },
        { text: 'Config. Clínica', icon: <SettingsIcon />, path: '/clinic/settings' },
        { text: 'Doctoralia', icon: <DoctoraliaIcon />, path: '/clinic/doctoralia' },
      ])
    },
    {
      text: 'Financeiro',
      icon: <AccountBalanceIcon />,
      hasSubmenu: true,
      isOpen: financeiroOpen,
      toggle: () => setFinanceiroOpen(!financeiroOpen),
      roles: ['admin', 'admin_master', 'financial', 'receptionist'] as SystemRole[],
      submenu: filterByRole([
        { text: 'Resumo', icon: <DashboardIcon />, path: '/financial/dashboard' },
        { text: 'DRE Gerencial', icon: <AssessmentIcon />, path: '/financial/dre' },
        { text: 'Plano de Contas', icon: <TreatmentPlanIcon />, path: '/financial/chart-of-accounts' },
        { text: 'Contas a Pagar', icon: <ExpenseIcon />, path: '/financial/accounts-payable' },
        { text: 'Despesas Fixas', icon: <RecurringIcon />, path: '/financial/accounts-payable?tab=fixed' },
        { text: 'Contas a Receber', icon: <TrendingUpIcon />, path: '/financial/accounts-receivable' },
        { text: 'Receitas Fixas', icon: <RevenueIcon />, path: '/financial/accounts-receivable?tab=fixed' },
        { text: 'Caixa', icon: <PointOfSaleIcon />, path: '/financial/cash-register' },
        { text: 'Auditoria', icon: <AuditIcon />, path: '/financial/audit' },
        { text: 'Extratos', icon: <WalletIcon />, path: '/financial/bank-statements' },
        { text: 'Repasses', icon: <RepasseIcon />, path: '/financial/repasses' },
        { text: 'Transações TEF', icon: <PaymentsIcon />, path: '/financial/tef' },
        { text: 'Notas Fiscais', icon: <InvoiceIcon />, path: '/financial/nfse' },
        { text: 'Empresas NF', icon: <StoreIcon />, path: '/financial/nfse-companies' },
        { text: 'Recibos', icon: <ReceiptsIcon />, path: '/financial/receipts' },
        { text: 'Cheques Recebidos', icon: <CheckInIcon />, path: '/financial/checks-received' },
        { text: 'Cheques Emitidos', icon: <CheckOutIcon />, path: '/financial/checks-issued' },
        { text: 'Cartões', icon: <CreditCardIcon />, path: '/financial/card-settlements' },
        { text: 'Descontos Pendentes', icon: <DiscountIcon />, path: '/financial/pending-discounts' },
        { text: 'Honorários', icon: <HonorariosIcon />, path: '/financial/professional-fees' },
      ])
    },
    {
      text: 'Estoque',
      icon: <InventoryModIcon />,
      hasSubmenu: true,
      isOpen: estoqueOpen,
      toggle: () => setEstoqueOpen(!estoqueOpen),
      roles: ['admin', 'admin_master', 'receptionist'] as SystemRole[],
      submenu: filterByRole([
        { text: 'Geral', icon: <InventoryModIcon />, path: '/inventory/items' },
        { text: 'Produto', icon: <ProductIcon />, path: '/inventory/items?type=produto' },
        { text: 'Material', icon: <MaterialMenuIcon />, path: '/inventory/items?type=material' },
        { text: 'Medicamento', icon: <MedicationIcon />, path: '/inventory/items?type=medicamento' },
        { text: 'Med. por Convênio', icon: <InsuranceMedIcon />, path: '/inventory/insurance-medications' },
        { text: 'Categorias', icon: <CategoryIcon />, path: '/inventory/categories' },
        { text: 'Localizações', icon: <LocationMenuIcon />, path: '/inventory/locations' },
        { text: 'Fabricantes', icon: <ManufacturerIcon />, path: '/inventory/manufacturers' },
        { text: 'Kits', icon: <KitMenuIcon />, path: '/inventory/kits' },
        { text: 'Movimentações', icon: <MovementIcon />, path: '/inventory/movements' },
        { text: 'Requisições', icon: <ReceiptIcon />, path: '/inventory/requisitions' },
      ])
    },
    { text: 'Relatórios', icon: <ReportsIcon />, path: '/clinic/reports', roles: ['admin', 'admin_master', 'professional', 'financial'] as SystemRole[] },
    { text: 'CRM', icon: <PeopleIcon />, path: '/crm', roles: ['admin', 'admin_master'] as SystemRole[] },
    { text: 'Unidades / Filiais', icon: <BusinessIcon />, path: '/admin/units', roles: ['admin', 'admin_master'] as SystemRole[] },
    { text: 'Gestão de Usuários', icon: <ProfessionalsIcon />, path: '/user-management', roles: ['admin_master'] as SystemRole[] },
    { text: 'Plano da plataforma', icon: <PaymentsIcon />, path: '/platform-payments', roles: ['admin_master'] as SystemRole[] },
    { 
      text: 'Config WhatsApp', 
      icon: <WhatsAppIcon />, 
      hasSubmenu: true,
      isOpen: whatsappAutoOpen,
      toggle: () => setWhatsappAutoOpen(!whatsappAutoOpen),
      roles: ['admin', 'admin_master'] as SystemRole[],
      submenu: [
        { text: 'Cloud API', icon: <CloudIcon />, path: '/whatsapp-cloud-api' },
        { text: 'Gerenciar APIs', icon: <WhatsAppIcon />, path: '/whatsapp-auto' },
        { text: 'QR Code', icon: <QrCodeIcon />, path: '/whatsapp-qr' },
        { text: 'Envio Manual', icon: <SendIcon />, path: '/whatsapp-manual' },
      ]
    },
    { text: 'Controle de Tarefas', icon: <KanbanIcon />, path: '/kanban', roles: ['admin', 'admin_master'] as SystemRole[] },
    { text: 'Remarketing', icon: <FollowUpIcon />, path: '/followups', roles: ['admin', 'admin_master'] as SystemRole[] },
    {
      text: 'Campanhas',
      icon: <CampaignIcon />,
      hasSubmenu: true,
      isOpen: campaignsOpen,
      toggle: () => setCampaignsOpen(!campaignsOpen),
      roles: ['admin', 'admin_master'] as SystemRole[],
      submenu: [
        { text: 'SMS', icon: <EmailIcon />, path: '/campaigns/sms' },
        { text: 'Campanhas de Email', icon: <EmailIcon />, path: '/campaigns/email-campaigns' },
        { text: 'Templates de Email', icon: <EmailIcon />, path: '/campaigns/email-templates' },
      ]
    },
    {
      text: 'Automações',
      icon: <AutoAwesomeIcon />,
      hasSubmenu: true,
      isOpen: automationsOpen,
      toggle: () => setAutomationsOpen(!automationsOpen),
      roles: ['admin', 'admin_master'] as SystemRole[],
      submenu: [
        { text: 'Automação de Email', icon: <EmailIcon />, path: '/automations/email' },
        { text: 'Automação de SMS', icon: <MessageIcon />, path: '/automations/sms' },
        { text: 'Templates SMS', icon: <MessageIcon />, path: '/automations/sms-templates' },
      ]
    },
    {
      text: 'Atendentes',
      icon: <PeopleIcon />,
      hasSubmenu: true,
      isOpen: attendantsOpen,
      toggle: () => setAttendantsOpen(!attendantsOpen),
      roles: ['admin', 'admin_master'] as SystemRole[],
      submenu: [
        { text: 'Gerenciar Atendentes', icon: <PeopleIcon />, path: '/attendants' },
        { text: 'Dashboard', icon: <AssessmentIcon />, path: '/attendants/dashboard' },
      ]
    },
    { text: 'Configurações', icon: <SettingsIcon />, path: '/settings', roles: ['admin', 'admin_master'] as SystemRole[] },
  ];

  // Filtrar menu baseado no perfil do usuário logado
  const menuItems = allMenuItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(userRole as SystemRole);
  }).filter(item => {
    // Se tem submenu, verificar se tem sub-itens visíveis
    if (item.hasSubmenu && item.submenu) {
      return item.submenu.length > 0;
    }
    return true;
  });

  const renderMenuItem = (item: any, index: number) => {
    // Para itens com submenu, verificar se algum subitem está ativo
    const isActive = item.hasSubmenu 
      ? item.submenu?.some((subItem: any) => {
          const subPathWithoutQuery = subItem.path?.split('?')[0] || subItem.path;
          const fullCurrentPath = `${location.pathname}${location.search}`;
          return fullCurrentPath === subItem.path ||
            location.pathname === subPathWithoutQuery ||
            (subPathWithoutQuery !== '/facebook-ads' && location.pathname.startsWith(subPathWithoutQuery));
        })
      : item.path === '/agenda'
        ? location.pathname === '/agenda'
        : location.pathname === item.path ||
          (item.path !== '/dashboard' && item.path !== '/crm' && location.pathname.startsWith(item.path));

    if (item.hasSubmenu) {
      const centralParentUnread =
        item.text === 'Central de Mensagens' && Array.isArray(item.submenu)
          ? item.submenu.reduce((acc: number, s: any) => acc + (Number(s.unreadCount) || 0), 0)
          : 0;

      return (
        <React.Fragment key={item.text}>
          <Tooltip title={!drawerOpen ? item.text : ''} placement="right" arrow>
            <ListItemButton
              onClick={drawerOpen ? item.toggle : handleDrawerExpand}
              className={`relative z-10 rounded-xl transition-all duration-700 ease-in-out ${
                drawerOpen ? 'mx-4 my-2 px-4 justify-start' : 'my-2 justify-center'
              } ${
                isActive 
                  ? 'text-gray-800' 
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
              sx={{
                backgroundColor: isActive ? 'rgba(20, 184, 166, 0.12)' : 'transparent',
                backdropFilter: isActive ? 'blur(10px)' : 'none',
                WebkitBackdropFilter: isActive ? 'blur(10px)' : 'none',
                border: isActive ? '1px solid rgba(20, 184, 166, 0.3)' : '1px solid transparent',
                ...(!drawerOpen && {
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  paddingLeft: '8px',
                  paddingRight: '8px',
                  width: 'fit-content',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }),
                '&:hover': {
                  backgroundColor: isActive 
                    ? 'rgba(20, 184, 166, 0.18)' 
                    : 'rgba(20, 184, 166, 0.07)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  borderColor: 'rgba(20, 184, 166, 0.2)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(20, 184, 166, 0.08)',
                  transform: 'translateY(-1px)',
                },
                '&::before': isActive ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '3px',
                  height: '80%',
                  background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
                  borderRadius: '0 2px 2px 0',
                } : {},
              }}
            >
              <ListItemIcon 
                sx={{
                  color: isActive ? '#0d9488 !important' : '#64748b',
                  minWidth: drawerOpen ? '40px' : 'auto',
                  justifyContent: drawerOpen ? 'flex-start' : 'center',
                  ...(!drawerOpen ? {
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.75rem',
                      color: isActive ? '#0d9488 !important' : '#64748b',
                    }
                  } : {
                    '& .MuiSvgIcon-root': {
                      color: isActive ? '#0d9488 !important' : '#64748b',
                    }
                  })
                }}
              >
                {item.text === 'Central de Mensagens' && !drawerOpen ? (
                  <MuiUnreadBadge
                    overlap="rectangular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    invisible={centralParentUnread === 0}
                    badgeContent={centralParentUnread > 99 ? '99+' : centralParentUnread}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.65rem',
                        height: 18,
                        minWidth: 18,
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {item.icon}
                    </Box>
                  </MuiUnreadBadge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
          {drawerOpen && (
            <>
              <ListItemText 
                primary={item.text}
                sx={{ 
                  margin: 0,
                  transition: 'opacity 0.4s ease-in-out, transform 0.4s ease-in-out',
                  animation: 'fadeIn 0.4s ease-in-out',
                  '@keyframes fadeIn': {
                    from: {
                      opacity: 0,
                      transform: 'translateX(-10px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateX(0)',
                    },
                  },
                      '& .MuiListItemText-primary': {
                        color: isActive ? '#0d9488 !important' : '#64748b',
                        fontWeight: isActive ? 700 : 600,
                        fontSize: '0.875rem',
                        margin: 0,
                        lineHeight: 1.5,
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                      }
                    }}
                  />
                  {item.text === 'Central de Mensagens' && centralParentUnread > 0 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.5,
                        mr: 0.5,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: '10px',
                        bgcolor: 'error.main',
                        color: 'error.contrastText',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        flexShrink: 0,
                      }}
                    >
                      {centralParentUnread > 99 ? '99+' : centralParentUnread}
                    </Box>
                  )}
                  {item.isOpen ? <ExpandLess className="text-gray-500" /> : <ExpandMore className="text-gray-500" />}
                </>
              )}
            </ListItemButton>
          </Tooltip>
          <Collapse 
            in={item.isOpen && drawerOpen} 
            timeout={300} 
            unmountOnExit 
            sx={{
              '& .MuiCollapse-wrapper': {
                display: 'block !important',
                width: '100% !important',
              },
              '& .MuiCollapse-wrapperInner': {
                display: 'block !important',
                width: '100% !important',
              },
              '& .MuiCollapse-root': {
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }
            }}
          >
            <List component="div" disablePadding sx={{ 
              width: '100%', 
              flexDirection: 'column',
              position: 'relative',
              zIndex: 1,
              backgroundColor: 'transparent',
              borderRadius: '12px',
              margin: '8px 4px 8px 4px',
              padding: '4px 0',
              border: '1px solid transparent',
              transition: 'all 0.3s ease',
              overflow: 'hidden',
            }}>
              {item.submenu.map((subItem: any) => {
                const subPathWithoutQuery = subItem.path?.split('?')[0] || subItem.path;
                const fullCurrentPath = `${location.pathname}${location.search}`;
                const isSubActive = fullCurrentPath === subItem.path || location.pathname === subPathWithoutQuery;
                const subUnread = Number(subItem.unreadCount) || 0;
                return (
                  <ListItem key={subItem.text} disablePadding sx={{ 
                    width: '100%',
                    position: 'relative',
                    zIndex: 1,
                    overflow: 'hidden',
                    minWidth: 0
                  }}>
                    <Tooltip title={!drawerOpen ? subItem.text : subItem.text} placement="right" arrow>
                      <ListItemButton
                        onClick={() => {
                          if (!drawerOpen) {
                            handleDrawerExpand();
                          }
                          navigate(subItem.path);
                        }}
                        className={`relative z-10 rounded-xl transition-all duration-200 ${
                          drawerOpen ? 'pl-8 my-1 justify-start' : 'my-1 justify-center'
                        } ${
                          isSubActive 
                            ? 'text-gray-800' 
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                        sx={{
                          backgroundColor: isSubActive 
                            ? 'rgba(20, 184, 166, 0.12)' 
                            : 'transparent',
                          backdropFilter: isSubActive ? 'blur(10px)' : 'none',
                          WebkitBackdropFilter: isSubActive ? 'blur(10px)' : 'none',
                          border: isSubActive 
                            ? '1px solid rgba(20, 184, 166, 0.3)' 
                            : '1px solid transparent',
                          overflow: 'hidden',
                          minWidth: 0,
                          width: '100%',
                          maxWidth: '100%',
                          paddingLeft: drawerOpen ? '16px' : '8px',
                          paddingRight: drawerOpen ? '20px' : '8px',
                          marginLeft: drawerOpen ? '4px' : 'auto',
                          marginRight: drawerOpen ? '20px' : 'auto',
                          ...(!drawerOpen && {
                            width: 'fit-content',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }),
                          '&:hover': {
                            backgroundColor: isSubActive 
                              ? 'rgba(20, 184, 166, 0.18)' 
                              : 'rgba(20, 184, 166, 0.07)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            borderColor: 'rgba(20, 184, 166, 0.2)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(20, 184, 166, 0.08)',
                            transform: 'translateY(-1px)',
                          },
                          '&::before': isSubActive ? {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '2px',
                            height: '80%',
                            background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
                            borderRadius: '0 1px 1px 0',
                          } : {},
                        }}
                      >
                        <ListItemIcon 
                          sx={{
                            color: isSubActive 
                              ? '#0d9488 !important' 
                              : '#64748b',
                            minWidth: drawerOpen ? '32px' : 'auto',
                            justifyContent: drawerOpen ? 'flex-start' : 'center',
                            transition: drawerOpen
                              ? 'all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important'
                              : 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important',
                            ...(!drawerOpen ? {
                              '& .MuiSvgIcon-root': {
                                fontSize: '1.75rem',
                                color: isSubActive 
                                  ? '#0d9488 !important' 
                                  : '#64748b',
                                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important',
                              }
                            } : {
                              '& .MuiSvgIcon-root': {
                                color: isSubActive 
                                  ? '#0d9488 !important' 
                                  : '#64748b',
                                transition: 'all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important',
                              }
                            })
                        }}
                      >
                          {item.text === 'Central de Mensagens' && !drawerOpen && subUnread > 0 ? (
                            <MuiUnreadBadge
                              overlap="rectangular"
                              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                              badgeContent={subUnread > 99 ? '99+' : subUnread}
                              color="error"
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.6rem',
                                  height: 16,
                                  minWidth: 16,
                                },
                              }}
                            >
                              <Box
                                component="span"
                                sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                {subItem.icon}
                              </Box>
                            </MuiUnreadBadge>
                          ) : (
                            subItem.icon
                          )}
                        </ListItemIcon>
                        {drawerOpen && (
                          <>
                            <ListItemText 
                              primary={subItem.text}
                              sx={{
                                margin: 0,
                                padding: 0,
                                overflow: 'hidden !important',
                                textOverflow: 'ellipsis !important',
                                whiteSpace: 'nowrap !important',
                                flex: '1 1 auto',
                                minWidth: 0,
                                maxWidth: '100%',
                                '& .MuiListItemText-primary': {
                                  color: isSubActive 
                                    ? '#0d9488 !important' 
                                    : '#64748b',
                                  fontWeight: isSubActive ? 700 : 600,
                                  fontSize: '0.875rem',
                                  margin: 0,
                                  padding: 0,
                                  lineHeight: 1.5,
                                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                                  overflow: 'hidden !important',
                                  textOverflow: 'ellipsis !important',
                                  whiteSpace: 'nowrap !important',
                                  display: 'block !important',
                                  width: '100%',
                                  maxWidth: '100%',
                                },
                                '& .MuiListItemText-root': {
                                  overflow: 'hidden !important',
                                  textOverflow: 'ellipsis !important',
                                  whiteSpace: 'nowrap !important',
                                  minWidth: 0,
                                }
                              }}
                            />
                            {item.text === 'Central de Mensagens' && subUnread > 0 && (
                              <Box
                                component="span"
                                sx={{
                                  ml: 0.5,
                                  mr: drawerOpen ? 1 : 0,
                                  px: 0.65,
                                  py: 0.2,
                                  borderRadius: '10px',
                                  bgcolor: 'error.main',
                                  color: 'error.contrastText',
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  lineHeight: 1.2,
                                  flexShrink: 0,
                                }}
                              >
                                {subUnread > 99 ? '99+' : subUnread}
                              </Box>
                            )}
                          </>
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <Tooltip title={!drawerOpen ? item.text : ''} placement="right" arrow>
        <ListItemButton
          onClick={() => {
            if (!drawerOpen) {
              handleDrawerExpand();
            }
            navigate(item.path);
          }}
          className={`relative z-10 rounded-xl transition-all duration-200 ${
            drawerOpen ? 'mx-4 my-2 px-4 justify-start' : 'my-2 justify-center'
          } ${
            isActive 
              ? 'text-gray-800' 
              : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
          }`}
          sx={{
            backgroundColor: isActive ? 'rgba(20, 184, 166, 0.12)' : 'transparent',
            backdropFilter: isActive ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: isActive ? 'blur(10px)' : 'none',
            border: isActive ? '1px solid rgba(20, 184, 166, 0.3)' : '1px solid transparent',
            ...(!drawerOpen && {
              marginLeft: 'auto',
              marginRight: 'auto',
              paddingLeft: '8px',
              paddingRight: '8px',
              width: 'fit-content',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }),
            '&:hover': {
              backgroundColor: isActive 
                ? 'rgba(20, 184, 166, 0.18)' 
                : 'rgba(20, 184, 166, 0.07)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              borderColor: 'rgba(20, 184, 166, 0.2)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(20, 184, 166, 0.08)',
              transform: 'translateY(-1px)',
            },
            '&::before': isActive ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '3px',
              height: '80%',
              background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)',
              borderRadius: '0 2px 2px 0',
            } : {},
          }}
        >
          <ListItemIcon 
            sx={{
              color: isActive ? '#0d9488 !important' : '#64748b',
              minWidth: drawerOpen ? '40px' : 'auto',
              justifyContent: drawerOpen ? 'flex-start' : 'center',
              transition: drawerOpen
                ? 'all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important'
                : 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important',
              ...(!drawerOpen ? {
                '& .MuiSvgIcon-root': {
                  fontSize: '1.75rem',
                  color: isActive ? '#0d9488 !important' : '#64748b',
                  transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important',
                }
              } : {
                '& .MuiSvgIcon-root': {
                  color: isActive ? '#0d9488 !important' : '#64748b',
                  transition: 'all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important',
                }
              })
            }}
          >
            {item.icon}
          </ListItemIcon>
          {drawerOpen && (
            <ListItemText 
              primary={item.text}
              sx={{ 
                margin: 0,
                '& .MuiListItemText-primary': {
                  color: isActive ? '#0d9488 !important' : '#64748b',
                  fontWeight: isActive ? 700 : 600,
                  fontSize: '0.875rem',
                  margin: 0,
                  lineHeight: 1.5,
                  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }
              }}
            />
          )}
        </ListItemButton>
      </Tooltip>
    );
  };

  const drawer = (
    <Box className="flex flex-col w-full h-full relative z-10" sx={{
      backgroundColor: '#ffffff',
      position: 'relative',
      paddingRight: '12px',
      paddingTop: { xs: 'env(safe-area-inset-top, 0px)', md: 0 },
      paddingBottom: { xs: 'env(safe-area-inset-bottom, 0px)', md: 0 },
      height: '100%',
      maxHeight: '100%',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      transition: drawerOpen 
        ? 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        : 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      '& > *': {
        transition: drawerOpen
          ? 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          : 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: '0px',
        width: '2px',
        height: '100%',
        background: 'linear-gradient(180deg, rgba(20, 184, 166, 0.4) 0%, rgba(20, 184, 166, 0.2) 50%, rgba(20, 184, 166, 0.4) 100%)',
        zIndex: 1,
        pointerEvents: 'none',
        boxShadow: '0 0 8px rgba(20, 184, 166, 0.15), 0 0 15px rgba(20, 184, 166, 0.08)',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '20%',
        right: 0,
        width: '100px',
        height: '60%',
        background: 'radial-gradient(ellipse at right, rgba(20, 184, 166, 0.06) 0%, transparent 70%)',
        zIndex: 0,
        pointerEvents: 'none',
        filter: 'blur(8px)',
      },
    }}>
      {/* Logo completa quando expandido */}
      <Box 
        className="flex items-center justify-center relative" 
        sx={{ 
          position: 'relative',
          paddingTop: drawerOpen ? '6px' : '0px',
          paddingBottom: drawerOpen ? '2px' : '0px',
          minHeight: drawerOpen ? 'auto' : '0px',
          height: drawerOpen ? 'auto' : '0px',
          lineHeight: 0,
          margin: 0,
          marginTop: drawerOpen ? '0px' : '0px',
          marginBottom: drawerOpen ? '0px' : '0px',
          overflow: 'hidden',
          zIndex: 0,
          transition: drawerOpen
            ? 'all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            : 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          '&::before': {
            content: '""',
            display: 'none',
          },
          '& .MuiBox-root': {
            margin: '0 !important',
            padding: '0 !important',
          },
        }}
      >
        <Box
          sx={{
            opacity: drawerOpen ? 1 : 0,
            transform: drawerOpen ? 'scale(1)' : 'scale(0.8)',
            transition: drawerOpen
              ? 'opacity 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              : 'opacity 1s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            pointerEvents: drawerOpen ? 'auto' : 'none',
          }}
        >
          <img
            src="/13.png"
            alt="Logo"
            onClick={handleDrawerExpand}
            style={{ 
              height: 'auto',
              maxHeight: '96px',
              width: 'auto',
              maxWidth: '200px',
              objectFit: 'contain',
              display: 'block',
              margin: 0,
              padding: 0,
              lineHeight: 0,
              verticalAlign: 'top',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
              e.currentTarget.style.transform = 'scale(0.98)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          />
        </Box>
      </Box>
      
      {/* Logo quando contraído */}
      {!drawerOpen && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingTop: '8px',
            paddingBottom: '8px',
            width: '100%',
            transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <img
            src="/10.png"
            alt="Logo"
            onClick={handleDrawerExpand}
            style={{ 
              height: 'auto',
              maxHeight: '44px',
              width: 'auto',
              maxWidth: '64px',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto',
              marginLeft: '50%',
              transform: 'translateX(calc(-50% + 16px))',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.8';
              e.currentTarget.style.transform = 'translateX(calc(-50% + 16px)) scale(0.98)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.transform = 'translateX(calc(-50% + 16px)) scale(1)';
            }}
          />
        </Box>
      )}
      
      {/* Lista com scroll estético */}
      <List className="flex-1 w-full overflow-y-auto overflow-x-hidden py-0 px-0 no-scrollbar" sx={{ 
        flex: 1,
        minHeight: 0,
        maxHeight: '100%',
        paddingTop: drawerOpen ? '4px !important' : '8px !important', 
        paddingBottom: '8px !important',
        marginTop: drawerOpen ? '0px' : '0px',
        position: 'relative',
        zIndex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: drawerOpen
          ? 'all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          : 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }}>
        {menuItems.map((item, index) => (
          <ListItem key={item.text} disablePadding sx={{ 
            width: '100%',
            display: 'block',
            position: 'relative'
          }}>
            {renderMenuItem(item, index)}
          </ListItem>
        ))}
      </List>
      
      {/* Perfil do usuário e botão de sair */}
      <Box sx={{
        flexShrink: 0,
        minHeight: 'fit-content',
        borderTop: '1px solid rgba(0,0,0,0.08)',
      }}>
        {/* Switcher de unidade (multi-unidade) */}
        <UnitSwitcher expanded={drawerOpen} />
        {/* Info do usuário */}
        {authUser && (
          <Box 
            sx={{ 
              px: drawerOpen ? 2 : 1, 
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              justifyContent: drawerOpen ? 'flex-start' : 'center',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              '&:hover': { backgroundColor: 'rgba(20, 184, 166, 0.05)' },
            }}
            onClick={() => navigate('/settings')}
          >
            <Avatar 
              src={authUser.avatar_url || undefined}
              sx={{ 
                width: 36, 
                height: 36, 
                bgcolor: '#14b8a6',
                fontSize: '0.85rem',
                fontWeight: 700,
                border: '2px solid rgba(20, 184, 166, 0.3)',
              }}
            >
              {authUser.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            {drawerOpen && (
              <Box sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#1e293b', 
                    fontSize: '0.8rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    lineHeight: 1.3,
                  }}
                >
                  {authUser.full_name || 'Usuário'}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'inline-block',
                    px: 0.8,
                    py: 0.1,
                    borderRadius: '4px',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    bgcolor: ROLE_COLORS[authUser.role]?.bg || '#f0fdf4',
                    color: ROLE_COLORS[authUser.role]?.color || '#16a34a',
                    border: `1px solid ${ROLE_COLORS[authUser.role]?.border || '#bbf7d0'}`,
                    lineHeight: 1.4,
                  }}
                >
                  {getRoleLabel(authUser.role)}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        <Box
          sx={{
            px: drawerOpen ? 2 : 1,
            pt: 1,
            pb: 2,
            display: 'flex',
            flexDirection: drawerOpen ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: drawerOpen ? 'flex-start' : 'center',
            gap: drawerOpen ? 0.5 : 0.75,
            flexWrap: 'wrap',
          }}
        >
          <LogoutButton variant="text" size="small" />
          {showCashRegisterShortcut && (
            drawerOpen ? (
              <Button
                variant="text"
                size="small"
                startIcon={<PointOfSaleIcon />}
                onClick={() => navigate(cashRegisterPath)}
                sx={{
                  color: isCashRegisterActive ? '#0f766e' : 'text.primary',
                  fontWeight: isCashRegisterActive ? 600 : 400,
                  bgcolor: isCashRegisterActive ? 'rgba(20, 184, 166, 0.08)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(20, 184, 166, 0.12)' },
                }}
              >
                Caixa
              </Button>
            ) : (
              <Tooltip title="Caixa" placement="right">
                <IconButton
                  size="small"
                  onClick={() => navigate(cashRegisterPath)}
                  aria-label="Caixa"
                  sx={{
                    color: isCashRegisterActive ? '#0f766e' : 'text.secondary',
                    bgcolor: isCashRegisterActive ? 'rgba(20, 184, 166, 0.12)' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(20, 184, 166, 0.16)' },
                  }}
                >
                  <PointOfSaleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )
          )}
        </Box>
      </Box>
    </Box>
  );

  const isInboxRoute = location.pathname.startsWith('/inbox');
  const isAgendaExpandidaRoute = location.pathname === '/agenda-expandida';
  const isFullBleedRoute = isInboxRoute || isAgendaExpandidaRoute;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Banner de notificação de pagamento */}
      <PaymentNotificationBanner />
      
      {/* Botão de menu mobile - flutuante (hidden on inbox routes - inbox has its own nav) */}
      {isMobile && !isInboxRoute && (
        <IconButton
          aria-label="open drawer"
          onClick={handleDrawerToggle}
          sx={{ 
            position: 'fixed',
            top: `calc(env(safe-area-inset-top, 0px) + ${bannerHeight + 12}px)`,
            left: `calc(env(safe-area-inset-left, 0px) + 12px)`,
            zIndex: theme.zIndex.drawer + 10,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'text.primary',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            width: 44,
            height: 44,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <MenuIcon />
        </IconButton>
      )}
      
      <Box
        component="nav"
        sx={{
          width: {
            md: `${(drawerOpen ? drawerWidth : drawerWidthCollapsed) + drawerMarginLeft}px`,
          },
          flexShrink: { md: 0 },
          overflow: { md: 'hidden' },
        }}
      >
        {/* Mobile Drawer */}
        <SwipeableDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          onOpen={() => setMobileOpen(true)}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: { xs: '280px', sm: drawerWidth },
              maxWidth: '85vw',
              height: '100%',
              overflow: 'hidden',
              backgroundColor: '#ffffff',
              borderRight: 'none',
              boxShadow: '4px 0 24px rgba(0, 0, 0, 0.12)',
              borderRadius: '0 20px 20px 0',
              top: 0,
              left: 0,
              position: 'relative',
              zIndex: theme.zIndex.drawer + 5,
            },
            '& .MuiBackdrop-root': {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            },
          }}
          swipeAreaWidth={20}
          disableSwipeToOpen={false}
        >
          {drawer}
        </SwipeableDrawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerOpen ? drawerWidth : drawerWidthCollapsed,
              height: `calc(100dvh - ${32 + bannerHeight}px)`,
              maxHeight: `calc(100dvh - ${32 + bannerHeight}px)`,
              overflow: 'hidden',
              backgroundColor: 'transparent',
              borderRight: 'none',
              border: '1px solid rgba(139, 92, 246, 0.15)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(139, 92, 246, 0.1), 0 0 40px rgba(139, 92, 246, 0.15), 0 0 80px rgba(139, 92, 246, 0.08)',
              transition: drawerOpen 
                ? 'width 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important'
                : 'width 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important',
              marginLeft: `${drawerMarginLeft}px`,
              marginTop: `${16 + bannerHeight}px`,
              marginBottom: '16px',
              borderRadius: '20px 0 0 20px',
              position: 'relative',
              zIndex: theme.zIndex.drawer,
              display: 'flex',
              flexDirection: 'column',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: '-20px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '60px',
                height: '80%',
                background: 'radial-gradient(ellipse at left, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
                filter: 'blur(20px)',
                zIndex: -1,
                pointerEvents: 'none',
              },
              '& > *': {
                transition: drawerOpen
                  ? 'all 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important'
                  : 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important',
              },
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
        <Box
        component="main"
        className="flex flex-col flex-1 relative"
        sx={{
          width: {
            md: `calc(100% - ${
              (drawerOpen ? drawerWidth : drawerWidthCollapsed) + drawerMarginLeft + mainContentGap
            }px)`,
          },
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          pt: {
            xs: (isMobile && isFullBleedRoute) ? 0 : `calc(env(safe-area-inset-top, 0px) + ${bannerHeight + 52}px)`,
            md: isFullBleedRoute ? `${16 + bannerHeight}px` : `${bannerHeight}px`,
          },
          position: 'relative',
          zIndex: 0,
          isolation: 'isolate',
          display: 'flex',
          flexDirection: 'column',
          height: '100dvh',
          overflow: 'hidden',
        }}
      >
        {/* Content area — sem padding superior na Agenda Expandida (ganha altura útil) */}
        <Box
          className="flex-1"
          sx={{
            overflowY: isFullBleedRoute ? 'hidden' : 'auto',
            overflowX: 'hidden',
            p: isFullBleedRoute ? 0 : (isMobile ? 1 : 2),
            pt: isFullBleedRoute ? 0 : (isMobile ? 1 : 2),
            pb: isFullBleedRoute ? 0 : (isMobile ? 1 : 2),
            display: isFullBleedRoute ? 'flex' : 'block',
            flexDirection: isFullBleedRoute ? 'column' : undefined,
            minHeight: isFullBleedRoute ? 0 : undefined,
          }}
        >
          <Outlet context={{ toggleDrawer: handleDrawerToggle }} />
        </Box>
      </Box>

      {/* Dialog de permissão de notificações */}
      {notificationSupported && (
        <NotificationPermissionDialog
          open={showNotificationDialog}
          onClose={handleCloseNotificationDialog}
          onPermissionGranted={handleNotificationPermissionGranted}
          onPermissionDenied={handleNotificationPermissionDenied}
        />
      )}

      <VirtualAssistantWidget />

    </Box>
  );
};

export default MainLayout;
